// Read-guard helpers for the play web app (Tom 2026-07-07).
//
// Every read-gated screen (Results, Stats, Groups, Profile, Leaderboard) shows a
// skeleton while its first Firestore / callable read is in flight. If that read
// STALLS — most often an App Check reCAPTCHA token that never mints or whose
// refresh hangs, which holds every Firestore read *pending* rather than erroring
// (firebase.js: "Without an App Check token EVERY Firestore read/write 401s") —
// the screen hangs on its skeleton forever, with no signal and no recourse but a
// hard refresh. (Play is exempt: it paints from the localStorage cache + a public
// CDN fast-paint, so it never blocks on a live read.)
//
// These helpers convert a stall into a catchable failure so the screen can flip
// to a "Couldn't load — Retry" state whose Retry re-fires the read (which
// re-triggers a fresh App Check token) and self-recovers — no manual reload.
//
// Extracted from the Leaderboard screen's original inline 8s-timeout+retry so all
// five screens share ONE pattern.

import { appCheckReady } from '../firebase.js';

const DEFAULT_TIMEOUT_MS = 8000;
// 3000 → 8000 (Tom 2026-07-28, seen live): a cold Safari load's reCAPTCHA
// script + first token mint routinely takes >3s. At 3s the pre-warm gave up,
// the read fired TOKENLESS into App-Check-enforced Firestore, failed fast
// with PERMISSION_DENIED, and the instant retry failed the same way (mint
// still in flight) — every read-gated tab landed on its error wall on first
// load, while "Try again" seconds later worked because the token had minted
// meanwhile. Still bounded — the never-hang design is unchanged.
const APP_CHECK_WAIT_MS = 8000;
// How long the retry leg pauses before re-reading. A first failure most
// often means "token mid-mint"; retrying instantly just fails twice in the
// same window. Costs nothing when the first attempt failed for other
// reasons (the wait below resolves immediately once appCheckReady settles).
const RETRY_BACKOFF_MS = 1500;

/**
 * Await the App Check pre-warm, but never for more than `ms` (funnel audit R1).
 * `appCheckReady` can fail to settle at all when the reCAPTCHA loader script
 * never fires (blocked script, flaky cell connection) — a bare `await` there is
 * the infinite-skeleton bug on the invite doors. Racing a resolved timeout
 * converts "token never minted" into "proceed and let the read itself fail
 * fast", which the read-timeout + retry below already handle. Never rejects.
 */
// Has the pre-warm ALREADY blown its budget once this session? (2026-08-11)
//
// The wait is bounded per call, which is not the same as bounded per session —
// and `guardedRead` awaits it TWICE, once before each attempt. When the token
// genuinely never mints (blocked reCAPTCHA script, dead cell connection) a
// single guarded read cost 8s + read + 8s + 1.5s + read = up to 33.5 SECONDS,
// and Profile renders a full-page "Loading…" for every one of them. That is
// the hang Tom hit tapping the Profile tab.
//
// Once we have watched the mint miss its window, waiting again teaches us
// nothing: the read fires tokenless, fails fast, and the existing retry
// handles it. So the timeout is remembered — and UNREMEMBERED the moment
// `appCheckReady` actually settles, so a slow-but-alive token still restores
// the normal path for the rest of the session rather than leaving the app
// permanently pessimistic.
let appCheckGaveUp = false;
appCheckReady
  .then(() => { appCheckGaveUp = false; })
  .catch(() => { /* a rejected pre-warm is a give-up, leave the flag alone */ });

export function appCheckReadyOrTimeout(ms = APP_CHECK_WAIT_MS) {
  if (appCheckGaveUp) return Promise.resolve();
  return Promise.race([
    appCheckReady,
    new Promise((resolve) => setTimeout(() => { appCheckGaveUp = true; resolve(); }, ms)),
  ]).catch(() => {});
}

/**
 * Race a promise against a timeout so a stalled read REJECTS instead of hanging.
 * The timer is always cleared (so a resolved read doesn't leak a pending timer).
 * @template T
 * @param {Promise<T>} promise
 * @param {number} ms
 * @param {string} label  surfaced in the rejection message for debugging
 * @returns {Promise<T>}
 */
export function withReadTimeout(promise, ms = DEFAULT_TIMEOUT_MS, label = 'read') {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`[readGuard] ${label} timed out after ${ms}ms`)),
      ms
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * Run `read` (a zero-arg thunk returning a promise), racing each attempt against
 * the timeout and retrying ONCE before giving up — the retry re-issues the read,
 * which prompts the SDK to mint a fresh App Check token. Rejects if both attempts
 * fail/stall, so the caller's existing try/catch flips to its error state.
 *
 * Awaits `appCheckReady` first so a cold first-token mint is surfaced rather than
 * silently pending. `appCheckReady` never rejects and is already-resolved when
 * App Check is off, so this can never hang on its own.
 *
 * @template T
 * @param {() => Promise<T>} read
 * @param {{ ms?: number, label?: string }} [opts]
 * @returns {Promise<T>}
 */
export async function guardedRead(read, { ms = DEFAULT_TIMEOUT_MS, label = 'read' } = {}) {
  // Bounded wait (R1): a pre-warm that never settles must not hang the read —
  // see appCheckReadyOrTimeout. Never throws.
  await appCheckReadyOrTimeout();
  try {
    return await withReadTimeout(read(), ms, label);
  } catch {
    // One retry — but not INSTANTLY (2026-07-28): give the App Check mint
    // that likely caused the failure time to land first. Both waits are
    // bounded/immediate when the token already exists, so a non-token
    // failure retries after only the short backoff.
    await appCheckReadyOrTimeout();
    await new Promise((resolve) => setTimeout(resolve, RETRY_BACKOFF_MS));
    return await withReadTimeout(read(), ms, label);
  }
}
